import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  MasterPaymentCard,
  PaymentCardBg,
  VisaPaymentCard,
} from "../icons/Icons";
import AddNewCardModal from "./modals/AddNewCardModal";

import {
  useAdminGetPaymentMethodsQuery,
  useAdminDeletePaymentMethodMutation,
  useAdminSetDefaultPaymentMethodMutation,
} from "../../services/organization/subscription";
import { toast } from "react-toastify";
import ConfirmationModal from "../Modals/ConfirmationModal";

const PaymentMethodsTab = () => {
  const { t } = useTranslation("subscription");
  const { data, isLoading, refetch } = useAdminGetPaymentMethodsQuery({refetchOnMountOrArgChange: true});
  const [deletePaymentMethod] = useAdminDeletePaymentMethodMutation();
  const [setDefaultPaymentMethod] = useAdminSetDefaultPaymentMethodMutation();

  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showSetDefaultModal, setShowSetDefaultModal] = useState(false);
  const [selectedPmId, setSelectedPmId] = useState(null);

  const paymentMethods = data?.data?.payment_methods || [];

  const handleRemoveCard = (id) => {
    setSelectedPmId(id);
    setShowRemoveModal(true);
    document.getElementById("remove-card-modal-trigger")?.click();
  };

  const confirmRemoveCard = async () => {
    try {
      await deletePaymentMethod(selectedPmId).unwrap();
      toast.success(t("paymentMethods.cardRemoved"));
    } catch (error) {
      toast.error(error?.data?.message || t("paymentMethods.removeFailed"));
    } finally {
      setShowRemoveModal(false);
      setSelectedPmId(null);
      document.getElementById("remove-card-modal-trigger")?.click();
    }
  };

  const handleSetDefault = (id) => {
    setSelectedPmId(id);
    setShowSetDefaultModal(true);
    document.getElementById("set-default-modal-trigger")?.click();
  };

  const confirmSetDefault = async () => {
    try {
      await setDefaultPaymentMethod(selectedPmId).unwrap();
      toast.success(t("paymentMethods.defaultUpdated", "Default payment method updated"));
    } catch (error) {
      toast.error(error?.data?.message || t("paymentMethods.updateFailed", "Failed to update default payment method"));
    } finally {
      setShowSetDefaultModal(false);
      setSelectedPmId(null);
      document.getElementById("set-default-modal-trigger")?.click();
    }
  };





  if (isLoading) {
    return (
      <div className="sub-tab-payment">
        <p className="sub-tab__description">{t("paymentMethods.manageMethods")}</p>
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sub-tab-payment">
      <p className="sub-tab__description">{t("paymentMethods.manageMethods")}</p>

      <div className="payment-cards-grid">
        {paymentMethods.map((card) => (
          <div
            key={card.id}
            className={`payment-card ${card.is_default ? "payment-card--selected" : ""}`}
            style={{ cursor: "default" }}
          >
            <div className="payment-card__bg">
              <PaymentCardBg />
            </div>
            <div className="payment-card__top">
              <span className="payment-card__type">
                {card.is_default ? t("paymentMethods.defaultCard") : t("paymentMethods.savedCard")}
              </span>
              {
                !card.is_default && (
                  <div className="payment-card__actions">
                    <button
                      className="payment-card__set-default-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(card.id);
                      }}
                    >
                      {t("paymentMethods.setDefault")}
                    </button>
                    <button
                      className="payment-card__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveCard(card.id);
                      }}
                      aria-label={t("paymentMethods.removeCard")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
              )}
            </div>

            <p className="payment-card__number">**** **** **** {card.last4}</p>

            <div className="payment-card__brand">
              {card.brand?.toLowerCase() === "mastercard" && (
                <div className="mastercard-logo">
                  <MasterPaymentCard width={62} height={48} color="#222357" />
                </div>
              )}
              {card.brand?.toLowerCase() === "visa" && (
                <div className="visa-logo">
                  <VisaPaymentCard width={97} height={32} color="#222357" />
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Add card tile */}
        <button
          className="payment-card payment-card--add"
          onClick={() => setShowAddCardModal(true)}
        >
          <div className="add-card-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#888"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
          <span className="add-card-label">{t("paymentMethods.addCard")}</span>
        </button>
      </div>
      {
        showAddCardModal && (
          <AddNewCardModal
            isOpened={showAddCardModal}
            onClose={() => setShowAddCardModal(false)}
          />
        )
      }


      <ConfirmationModal
        modalId="remove-card-modal"
        isOpened={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
        handleConfirmation={confirmRemoveCard}
        confirmationMessage={t("paymentMethods.confirmRemove")}
      />

      <ConfirmationModal
        modalId="set-default-modal"
        isOpened={showSetDefaultModal}
        onClose={() => setShowSetDefaultModal(false)}
        handleConfirmation={confirmSetDefault}
        confirmationMessage={t("paymentMethods.setDefaultConfirm")}
      >
        <p className="mt-2 text-muted">{t("paymentMethods.setDefaultDescription")}</p>
      </ConfirmationModal>

      <button
        id="remove-card-modal-trigger"
        style={{ display: "none" }}
        data-bs-toggle="modal"
        data-bs-target="#remove-card-modal"
      />

      <button
        id="set-default-modal-trigger"
        style={{ display: "none" }}
        data-bs-toggle="modal"
        data-bs-target="#set-default-modal"
      />
    </div>
  );
};

export default PaymentMethodsTab;
